/**
 * `DIALOG.templates` (W2-05 easing-templates-raw; DESIGN.md §4.13): motion templates for a line starting at a beat.
 * A non-modal side sheet on the left (W3-05, UX-040): the docked preview that loops the span stays visible and the
 * timeline stays usable while the template is tweaked; Escape closes it from anywhere (easing.module.tsx).
 *
 * The chosen template runs as a live Gesture (`applyTemplate`, built from `Core.keys`) on every parameter change,
 * so the preview, the motion columns and the inspector show the result while the playhead loops the span; Apply
 * (or Enter, Ctrl+Enter from a field being typed in: applyKeys.ts) records ONE undoable step, Cancel / Esc leaves no
 * trace. Notes of the template (e.g. an offset layer whose base changes later) are listed under the fields. Parameters are shown in friendly units (opacity in %,
 * pulse size in %), the curve is chosen with the easing picker, and at the Full level the motion can go on a new
 * additive layer. The last parameters of each template are remembered (`settings.ext['easing.templateParams']`).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { DialogProps } from '../../app/registry/types';
import { DIALOG } from '../../app/dialogs/types';
import type { Ease as EaseValue } from '../../core/ease/types';
import type { LineId } from '../../core/model/types';
import { buildTempoMap } from '../../core/doc/container';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat, BeatParseContext } from '../../core/time/types';
import type { Json, MessageRef, RGB } from '../../core/util/types';
import {
  applyTemplate,
  formatTemplateColor,
  parseTemplateColor,
  SLIDE_DISTANCE_X,
  SLIDE_DISTANCE_Y,
  TEMPLATE_DEFAULT_EASE,
  TEMPLATE_IDS,
  TEMPLATE_SPECS,
} from '../../core/tools/templates';
import type { TemplateId, TemplateParams, ToolReport } from '../../core/tools/types';
import {
  BeatField,
  Button,
  Checkbox,
  ColorField,
  CurveThumb,
  Icon,
  IconButton,
  NumberField,
  Segmented,
  Sheet,
} from '../../ui';
import { useReducedMotion } from '../../ui/internal/useReducedMotion';
import { useApplyKeys } from './applyKeys';
import { easeName } from './EasingPicker';
import s from './Easing.module.css';
import { useDocVersion, useEditorSlice, useServicesStore, useSettings, useText } from './hooks';
import { createLivePreview, type LivePreview } from './livePreview';
import { m } from './templates.messages';

type ParamValues = Readonly<Record<string, number | string | boolean>>;

interface Outcome {
  readonly reason: MessageRef | null;
  readonly notes: readonly MessageRef[];
  readonly changed: number;
}

export const TEMPLATE_ICONS: Readonly<Record<TemplateId, string>> = {
  fadeIn: 'Eye',
  fadeOut: 'EyeOff',
  slideIn: 'ArrowRightToLine',
  slideOut: 'ArrowRightFromLine',
  moveTo: 'Move',
  spin: 'RotateCw',
  swing: 'Activity',
  pulse: 'HeartPulse',
  shake: 'Vibrate',
  flash: 'Zap',
  orbitParent: 'Orbit',
};

/** Remembered parameters per template. */
export const TEMPLATE_PARAMS_KEY = 'easing.templateParams';

function storedParams(raw: Json | undefined, id: TemplateId): ParamValues {
  const defaults = TEMPLATE_SPECS[id].defaults;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return defaults;
  const own = (raw as Readonly<Record<string, Json>>)[id];
  if (typeof own !== 'object' || own === null || Array.isArray(own)) return defaults;
  const out: Record<string, number | string | boolean> = { ...defaults };
  for (const [k, v] of Object.entries(own as Readonly<Record<string, Json>>)) {
    if (k in defaults && (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean')) out[k] = v;
  }
  return out;
}

/**
 * A point picked in the preview (TemplatesProps.point, CR-W2-05-3): "Move here" goes there, a slide comes from /
 * goes to it. Templates without x / y keep their parameters.
 */
function withPoint(params: ParamValues, point: readonly [number, number] | undefined): ParamValues {
  if (point === undefined || !('x' in params) || !('y' in params)) return params;
  const out: Record<string, number | string | boolean> = { ...params, x: point[0], y: point[1] };
  if ('from' in params && typeof params.from === 'string') out.from = 'point';
  if ('to' in params && typeof params.to === 'string') out.to = 'point';
  return out;
}

const PCT = 255 / 100;

export function TemplatesDialog(props: DialogProps<'templates'>) {
  const { services } = props;
  const { t, tr } = useText();
  const store = useServicesStore(services);
  useDocVersion(store);
  const level = useEditorSlice(services, (st) => st.level);
  const snapDiv = useEditorSlice(services, (st) => st.snap.div);
  const settings = useSettings(services);
  const reduced = useReducedMotion();
  const line: LineId = props.props.line;
  const [template, setTemplate] = useState<TemplateId>(props.props.initial ?? 'fadeIn');
  const [paramsById, setParamsById] = useState<Partial<Record<TemplateId, ParamValues>>>({});
  const [at, setAt] = useState<Beat>(props.props.at);
  const [duration, setDuration] = useState<Beat>(Beats.fromInt(2));
  const [ease, setEase] = useState<EaseValue>(TEMPLATE_DEFAULT_EASE);
  const [asNewLayer, setAsNewLayer] = useState(false);
  const [loop, setLoop] = useState(true);
  const [outcome, setOutcome] = useState<Outcome>({ reason: null, notes: [], changed: 0 });
  /** The outcome of the update the gesture shows now (the state follows one render later). */
  const shown = useRef<Outcome>(outcome);
  const live = useRef<LivePreview | null>(null);
  const spec = TEMPLATE_SPECS[template];
  const storedRaw = settings.ext[TEMPLATE_PARAMS_KEY];
  const own = paramsById[template];
  const point = props.props.point;
  const params = useMemo(
    () => own ?? withPoint(storedParams(storedRaw, template), point),
    [own, storedRaw, template, point],
  );
  const lineName = store?.doc.lines.get(line)?.name ?? '';
  const layered = level === 'full' && spec.layered && asNewLayer;

  const tempo = store === null ? null : buildTempoMap(store.doc, store.idx);
  const beatCtx: BeatParseContext = {
    snapDiv,
    syntax: settings.editing.beatSyntax,
    ...(tempo === null ? {} : { tempo }),
  };
  const end = Beats.add(at, duration);
  const t0 = tempo?.secAt(at) ?? null;
  const t1 = tempo?.secAt(end) ?? null;

  const full: TemplateParams = useMemo(
    () => ({ template, line, at, duration, ease, params, asNewLayer: layered }),
    [template, line, at, duration, ease, params, layered],
  );

  // One live preview (gesture) per template, so the history label names it.
  useEffect(() => {
    const preview = createLivePreview(services, {
      meta: { label: { key: m.txApply, params: { name: t(`templates.name.${template}`) } }, command: 'templates.open' },
      loop: !reduced && loop,
    });
    live.current = preview;
    return () => {
      preview.end();
      live.current = null;
    };
  }, [services, template, reduced, loop, t]);

  useEffect(() => {
    const preview = live.current;
    if (preview === null) return;
    const span = t0 === null || t1 === null ? null : { t0, t1 };
    const out = preview.show((tx): ToolReport => applyTemplate(tx, full), span);
    const next: Outcome =
      out === null
        ? { reason: { key: m.noLine }, notes: [], changed: 0 }
        : !out.ok
          ? { reason: out.reason, notes: [], changed: 0 }
          : { reason: null, notes: [...(out.value.notes ?? []), ...out.notes], changed: out.value.changed };
    shown.current = next;
    setOutcome(next);
  }, [full, t0, t1, template, reduced, loop]);

  const setParam = (key: string, value: number | string | boolean): void => {
    setParamsById((cur) => ({ ...cur, [template]: { ...params, [key]: value } }));
  };

  const finish = (applied: boolean): void => {
    const preview = live.current;
    const result = shown.current;
    if (!applied || preview === null || result.reason !== null) {
      preview?.end();
      props.close(null);
      return;
    }
    const txn = preview.commit();
    live.current = null;
    if (txn === null) {
      props.close(null);
      return;
    }
    const raw = settings.ext[TEMPLATE_PARAMS_KEY];
    const stored =
      typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Readonly<Record<string, Json>>) : {};
    services.settings.setExt(TEMPLATE_PARAMS_KEY, { ...stored, [template]: { ...params } });
    services.ui.toast(
      { key: m.applied, params: { name: t(`templates.name.${template}`), count: result.changed } },
      { kind: 'success', undo: true },
    );
    props.close({ applied: true });
  };

  const num = (
    key: string,
    label: string,
    opts: { unit?: string; min?: number; max?: number; step?: number; scale?: number; precision?: number } = {},
  ) => {
    const scale = opts.scale ?? 1;
    const raw = params[key];
    const value = typeof raw === 'number' ? raw / scale : Number(raw ?? 0) / scale;
    return (
      <NumberField
        key={key}
        label={label}
        value={{ kind: 'value', value }}
        {...(opts.unit === undefined ? {} : { unit: opts.unit })}
        {...(opts.min === undefined ? {} : { min: opts.min })}
        {...(opts.max === undefined ? {} : { max: opts.max })}
        step={opts.step ?? 1}
        precision={opts.precision ?? 2}
        onCommit={(v) => {
          if (v.kind === 'number') setParam(key, Math.round(v.value * scale * 1e6) / 1e6);
        }}
      />
    );
  };

  const direction = (
    <Segmented
      key="dir"
      label={t(m.direction)}
      value={params.dir === -1 ? 'ccw' : 'cw'}
      options={[
        { value: 'cw', label: t(m.clockwise), icon: 'RotateCw' },
        { value: 'ccw', label: t(m.counterClockwise), icon: 'RotateCcw' },
      ]}
      onChange={(v) => {
        setParam('dir', v === 'ccw' ? -1 : 1);
      }}
    />
  );

  const sides = (key: 'from' | 'to') => (
    <Segmented
      key={key}
      label={t(key === 'from' ? m.sideIn : m.sideOut)}
      value={String(params[key] ?? 'left') as 'left' | 'right' | 'top' | 'bottom' | 'point'}
      options={(['left', 'right', 'top', 'bottom', 'point'] as const).map((side) => ({
        value: side,
        label: t(`templates.side.${side}`),
      }))}
      onChange={(v) => {
        setParamsById((cur) => ({
          ...cur,
          [template]: {
            ...params,
            [key]: v,
            ...(v === 'left' || v === 'right'
              ? { distance: SLIDE_DISTANCE_X }
              : v === 'top' || v === 'bottom'
                ? { distance: SLIDE_DISTANCE_Y }
                : {}),
          },
        }));
      }}
    />
  );

  const usePosition = (
    <Button
      key="usePos"
      size="sm"
      variant="ghost"
      icon="Crosshair"
      onClick={() => {
        const engine = services.engine;
        if (engine === null || t0 === null) return;
        setParamsById((cur) => ({
          ...cur,
          [template]: {
            ...params,
            x: Math.round(engine.channelValue(line, 'moveX', t0, 'sum') * 100) / 100 || 0,
            y: Math.round(engine.channelValue(line, 'moveY', t0, 'sum') * 100) / 100 || 0,
          },
        }));
      }}
    >
      {t(m.usePosition)}
    </Button>
  );

  const axes = (
    <Segmented
      key="axes"
      label={t(m.axes)}
      value={String(params.axes ?? 'both') as 'both' | 'x' | 'y'}
      options={(['both', 'x', 'y'] as const).map((a) => ({ value: a, label: t(`templates.axes.${a}`) }))}
      onChange={(v) => {
        setParam('axes', v);
      }}
    />
  );

  const fields = (): ReactNode[] => {
    switch (template) {
      case 'fadeIn':
        return layered
          ? [num('from', t(m.opacityFrom), { unit: '%', min: -100, max: 100, scale: PCT })]
          : [
              num('from', t(m.opacityFrom), { unit: '%', min: -100, max: 100, scale: PCT }),
              num('to', t(m.opacityTo), { unit: '%', min: -100, max: 100, scale: PCT }),
            ];
      case 'fadeOut':
        return [num('to', t(m.opacityTo), { unit: '%', min: -100, max: 100, scale: PCT })];
      case 'slideIn':
      case 'slideOut': {
        const key = template === 'slideIn' ? 'from' : 'to';
        const side = params[key];
        return side === 'point'
          ? [sides(key), num('x', t(m.pointX), { unit: 'px' }), num('y', t(m.pointY), { unit: 'px' }), usePosition]
          : [sides(key), num('distance', t(m.distance), { unit: 'px', min: 0 })];
      }
      case 'moveTo':
        return [num('x', t(m.pointX), { unit: 'px' }), num('y', t(m.pointY), { unit: 'px' }), usePosition];
      case 'spin':
        return [num('turns', t(m.turns), { min: 0.01, step: 0.25 }), direction];
      case 'swing':
        return [
          num('amplitude', t(m.amplitude), { unit: 'deg', min: 0 }),
          num('swings', t(m.swings), { min: 1, max: 64, precision: 0 }),
          direction,
          <Checkbox
            key="settle"
            label={t(m.settle)}
            checked={params.settle === true}
            onChange={(v) => {
              setParam('settle', v);
            }}
          />,
        ];
      case 'pulse':
        return [
          num('amount', t(m.amount), { unit: '%', min: 1, scale: 0.01, step: 5 }),
          num('count', t(m.pulses), { min: 1, max: 64, precision: 0 }),
          axes,
        ];
      case 'shake':
        return [
          num('amplitude', t(m.amplitude), { unit: 'px', min: 0 }),
          num('count', t(m.shakes), { min: 2, max: 512, precision: 0 }),
          axes,
          <div key="seed" className={s.inlineRow}>
            {num('seed', t(m.seed), { precision: 0 })}
            <IconButton
              icon="Dices"
              size="sm"
              label={{ key: m.newSeed }}
              onClick={() => {
                setParam('seed', Math.floor(Math.random() * 100000));
              }}
            />
          </div>,
          <Checkbox
            key="settle"
            label={t(m.settle)}
            checked={params.settle === true}
            onChange={(v) => {
              setParam('settle', v);
            }}
          />,
        ];
      case 'flash': {
        const c: RGB = parseTemplateColor(String(params.color ?? '#ffffff')) ?? [255, 255, 255];
        return [
          <ColorField
            key="color"
            label={t(m.color)}
            value={{ kind: 'value', value: c }}
            onCommit={(v) => {
              if (v !== null) setParam('color', formatTemplateColor(v));
            }}
          />,
        ];
      }
      case 'orbitParent':
        return [
          num('turns', t(m.turns), { min: 0.01, step: 0.25 }),
          direction,
          num('radius', t(m.radius), { unit: 'px', min: 0 }),
          <p key="radiusHelp" className={s.note}>
            {t(m.radiusHelp)}
          </p>,
        ];
    }
  };

  // Enter applies (Ctrl+Enter from any field), Esc cancels (the Dialog).
  const keys = useApplyKeys(() => {
    finish(true);
  }, outcome.reason !== null);

  const chooseCurve = async (): Promise<void> => {
    const chosen = await services.ui.openDialog(DIALOG.easingPicker, { segments: [], initial: ease, use: 'general' });
    if (chosen !== null) setEase(chosen);
  };

  return (
    <Sheet
      open
      side="left"
      title={t(m.title)}
      onClose={() => {
        finish(false);
      }}
      footer={
        <div className={s.keysScope} {...keys}>
          <Button
            variant="ghost"
            onClick={() => {
              finish(false);
            }}
          >
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            disabledReason={outcome.reason}
            onClick={() => {
              finish(true);
            }}
          >
            {t(m.apply)}
          </Button>
        </div>
      }
    >
      <div className={s.templates} {...keys} data-templates-sheet="">
        <p className={s.note}>{t(m.target, { line: lineName, beat: formatBeat(at, settings.editing.beatDisplay) })}</p>
        <div className={s.templateChooser} role="radiogroup" aria-label={t(m.chooserLabel)}>
          {TEMPLATE_IDS.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={id === template}
              className={s.templateButton}
              data-template={id}
              tabIndex={id === template ? 0 : -1}
              onClick={() => {
                setTemplate(id);
              }}
              onKeyDown={(e) => {
                const i = TEMPLATE_IDS.indexOf(id);
                const delta =
                  e.key === 'ArrowRight' || e.key === 'ArrowDown'
                    ? 1
                    : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
                      ? -1
                      : 0;
                if (delta === 0) return;
                e.preventDefault();
                const next = TEMPLATE_IDS[(i + delta + TEMPLATE_IDS.length) % TEMPLATE_IDS.length] ?? id;
                setTemplate(next);
                const el = e.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-template="${next}"]`);
                el?.focus();
              }}
            >
              <Icon name={TEMPLATE_ICONS[id]} size={16} />
              <span>{t(`templates.name.${id}`)}</span>
            </button>
          ))}
        </div>
        <p className={s.note}>{t(`templates.about.${template}`)}</p>
        <div className={s.templateFields}>
          <BeatField
            label={t(m.start)}
            value={{ kind: 'value', value: at }}
            ctx={beatCtx}
            echo
            onCommit={(b) => {
              if (Beats.cmp(b, Beats.ZERO) >= 0) setAt(b);
            }}
          />
          <BeatField
            label={t(m.duration)}
            value={{ kind: 'value', value: duration }}
            ctx={{ snapDiv, syntax: settings.editing.beatSyntax }}
            onCommit={(b) => {
              if (Beats.cmp(b, Beats.ZERO) > 0) setDuration(b);
            }}
          />
          {spec.usesEase ? (
            <div className={s.curveRow}>
              <span className={s.fieldLabel}>{t(m.curve)}</span>
              <CurveThumb ease={ease} size={28} />
              <span>{easeName(ease, { t, tr })}</span>
              <Button size="sm" icon="Spline" onClick={() => void chooseCurve()}>
                {t(m.curveChange)}
              </Button>
            </div>
          ) : (
            <p className={s.note}>{t(m.curveFixed)}</p>
          )}
          {fields()}
          {level === 'full' && (
            <Checkbox
              label={t(m.asNewLayer)}
              checked={spec.layered && asNewLayer}
              disabledReason={spec.layered ? null : { key: m.asNewLayerNo }}
              onChange={setAsNewLayer}
            />
          )}
          {level === 'full' && spec.layered && <p className={s.note}>{t(m.asNewLayerHelp)}</p>}
        </div>
        <div className={s.templateFooterNote}>
          <Checkbox label={t(m.previewOff)} checked={loop} onChange={setLoop} />
          <p className={s.note}>{t(m.previewNote)}</p>
        </div>
        {outcome.reason !== null && (
          <p className={s.error} role="alert">
            {t(m.failed, { reason: tr(outcome.reason) })}
          </p>
        )}
        {outcome.notes.length > 0 && (
          <ul className={s.notes} role="status">
            {outcome.notes.map((n, i) => (
              <li key={`${n.key}-${String(i)}`}>
                <Icon name="Info" size={12} /> {tr(n)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
